import { IClosure } from '@store/features/closure/closure.types'
import { Box, FormHelperText, SelectChangeEvent, MenuItem, Typography } from '@mui/material'
import {
  TimeSelect,
  StyledBox
} from '@pages/CreateTicket/book-ticket/components/BookTicketStepper/components/SelectDateStep/styled'
import dayjs from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { selectDateDayHours } from '@pages/CreateTicket/book-ticket/helpers/selectDateHelpers'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMemo, useState } from 'react'
import { Button } from '@components/common'
import { DatePicker } from '@mui/x-date-pickers'
import { FormWrapper, RowWrapper, Desc } from '@components/ClosuresDialog/styled'
import API from '@services'
import { DemoItem } from '@mui/x-date-pickers/internals/demo'
import { ClosureSchema, closureSchema } from './schema'

dayjs.extend(utc)
dayjs.extend(timezone)

interface Props {
  closure?: IClosure
  close: () => void
  courseId: string
  reload: () => void
}

function combineDateAndTime(dateInput: string | Date | dayjs.Dayjs, timeStr: string) {
  const [hours, minutesPart] = timeStr.split(':')
  const [minutes, period] = minutesPart.split(' ')
  let hour = parseInt(hours, 10)
  if (period === 'PM' && hour !== 12) hour += 12
  if (period === 'AM' && hour === 12) hour = 0

  const base = dayjs.tz(dateInput, 'America/New_York')
    .set('hour', hour)
    .set('minute', parseInt(minutes, 10))
    .set('second', 0)
    .set('millisecond', 0)

  return base.format('YYYY-MM-DD HH:mm:ss.SSS')
}

function getTimeFromDate(date: dayjs.Dayjs) {
  const pad = (n: number) => n.toString().padStart(2, '0')
  const rawHours = date.hour()
  const minutes = pad(date.minute())
  const period = rawHours >= 12 ? 'PM' : 'AM'
  const hours12 = rawHours % 12 === 0 ? 12 : rawHours % 12
  return `${pad(hours12)}:${minutes} ${period}`
}

const ClosuresForm = ({ close, closure, courseId, reload }: Props) => {
  const [loading, setLoading] = useState(false)

  const getDefaultValues = () => {
    if (!closure) return {}

    const startDate = dayjs.tz(closure.startDate, 'America/New_York')
    const endDate = dayjs.tz(closure.endDate, 'America/New_York')

    const startTime = getTimeFromDate(startDate)
    const endTime = getTimeFromDate(endDate)

    return {
      startDate,
      startTime,
      endDate,
      endTime
    } as any
  }

  const {
    setValue,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<ClosureSchema>({
    defaultValues: getDefaultValues(),
    // @ts-ignore
    resolver: yupResolver(closureSchema)
  })

  const { startDate, startTime, endDate, endTime } = useWatch({
    control
  })

  const submitHandler: SubmitHandler<ClosureSchema> = async (data) => {
    setLoading(true)

    try {
      const startDate = combineDateAndTime(data.startDate, data.startTime)
      const endDate = combineDateAndTime(data.endDate, data.endTime)

      const payload = {
        startDate,
        endDate,
        courseId
      }

      if (closure?.id) {
        await API.closure.update({ id: closure.id, ...payload })
      } else {
        await API.closure.createClosure(payload)
      }

      reload()
    } catch (err) {
      console.log('Error creating or updating closeure => ', err)
    } finally {
      setLoading(false)
    }
  }

  const handleStartTimeChange = (event: SelectChangeEvent) => {
    setValue('startTime', event.target.value, {
      shouldValidate: true
    })
  }

  const handleEndTimeChange = (event: SelectChangeEvent) => {
    setValue('endTime', event.target.value, {
      shouldValidate: true
    })
  }

  const timeOptions = useMemo(
    () =>
      selectDateDayHours.map((option) => {
        return {
          ...option,
          disabled: false
        }
      }),
    []
  )

  const onSubmit = handleSubmit(submitHandler)

  return (
    <FormWrapper>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            marginBottom: '32px'
          }}
        >
          <Typography component='h4' variant='h4' align='center'>
            {closure ? 'Edit Closure Time' : 'Create Closure'}
          </Typography>

          {closure && <Desc>{`Select the date and time to start\n and end course closure.`}</Desc>}
        </Box>
        <RowWrapper>
          <Box>
            <DemoItem label='Start Date'>
              <DatePicker
                className='data-picker'
                sx={{
                  input: {
                    border: 'none !important'
                  }
                }}
                defaultValue={startDate}
                onChange={(newDate) => {
                  setValue('startDate', newDate as any, {
                    shouldValidate: !startTime
                  })
                  setValue('startTime', '')
                }}
              />
            </DemoItem>
            <FormHelperText error>{errors?.startDate?.message}</FormHelperText>
          </Box>
          <StyledBox>
            <TimeSelect
              data-testid='time-select'
              title='Start Time'
              label=''
              value={startTime}
              onChange={handleStartTimeChange}
              error={!!errors?.startTime?.message}
              helperText={errors?.startTime?.message}
              MenuProps={{ PaperProps: { sx: { maxHeight: '260px' } } }}
              inputProps={{
                placeholder: 'Choose time'
              }}
            >
              {timeOptions.map(({ value, disabled }) => (
                <MenuItem key={value} value={value} disabled={disabled}>
                  {value}
                </MenuItem>
              ))}
            </TimeSelect>
          </StyledBox>
        </RowWrapper>
        <RowWrapper className='second-row'>
          <Box>
            <DemoItem label='End Date'>
              <DatePicker
                className='data-picker'
                sx={{
                  input: {
                    border: 'none !important'
                  }
                }}
                defaultValue={endDate}
                onChange={(newDate) => {
                  setValue('endDate', newDate as any, {
                    shouldValidate: !startTime
                  })
                  setValue('endTime', '')
                }}
              />
            </DemoItem>
            <FormHelperText error>{errors?.endDate?.message}</FormHelperText>
          </Box>
          <StyledBox>
            <TimeSelect
              data-testid='time-select'
              title='End time'
              label=''
              value={endTime}
              onChange={handleEndTimeChange}
              error={!!errors?.endTime?.message}
              helperText={errors?.endTime?.message}
              MenuProps={{ PaperProps: { sx: { maxHeight: 260 } } }}
              inputProps={{
                placeholder: 'Choose Time'
              }}
            >
              {timeOptions.map(({ value, disabled }) => (
                <MenuItem key={value} value={value} disabled={disabled}>
                  {value}
                </MenuItem>
              ))}
            </TimeSelect>
          </StyledBox>
        </RowWrapper>
        <Box
          sx={{
            position: 'relative',
            width: 'calc(100% + 48px)',
            height: '1px',
            background: '#dadada',
            left: '-24px'
          }}
        />
        {closure ? (
          <Box
            sx={{
              marginTop: '24px',
              display: 'grid',
              gap: '16px',
              gridTemplateColumns: '1fr 1fr'
            }}
          >
            <Button color='primary' variant='contained' onClick={onSubmit}>
              Edit
            </Button>
            <Button color='primary' variant='outlined' onClick={() => close()}>
              Cancel
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              marginTop: '24px'
            }}
          >
            <Button isLoading={loading} color='primary' onClick={onSubmit} className='create-edit-button'>
              Create Closure
            </Button>
          </Box>
        )}
      </LocalizationProvider>
    </FormWrapper>
  )
}

export default ClosuresForm
